import { useEffect, useState } from "react";
import { useParams, NavLink, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { BookOpenText, FileText, Loader2, AlertTriangle, Workflow, MessageSquare } from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.4.13 — In-shell ops documentation viewer.
 *
 * Renders the /app/memory/ markdown ops docs inside the admin shell.
 * Tabs across the available docs at the top, scroll-only body with
 * a fixed-width prose container (chrome-styled to match the rest of
 * the admin shell).
 *
 * Brief 3.4.13.1 — heading anchors: each h1-h6 gets an `id` matching
 * the slugified text so the Cmd-K palette can navigate to the right
 * place via `/admin/docs/manual/{slug}#{anchor}`. Hash-scroll on
 * mount + on every location change picks up the URL fragment.
 *
 * Brief 3.4.13.2 — telegram operations manual: added as a third tab.
 *
 * Routes: /admin/docs/manual → defaults to compliance-manual.
 *         /admin/docs/manual/:slug → specific doc by slug.
 *
 * Admin-only via the AdminLayout outlet gate.
 */

const TABS = [
  { slug: "compliance-manual", label: "Compliance manual", icon: BookOpenText },
  { slug: "telegram-manual", label: "Telegram operations", icon: MessageSquare },
  { slug: "telegram-status", label: "Telegram status", icon: Workflow },
];


// Mirror of the backend `_slugify` so anchor IDs match exactly.
function slugify(text) {
  if (typeof text !== "string") {
    // react-markdown passes children as an array of React nodes for
    // headings with inline formatting — flatten to string first.
    if (Array.isArray(text)) return slugify(text.join(""));
    return "";
  }
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function flattenChildren(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(flattenChildren).join("");
  if (children && typeof children === "object" && "props" in children) {
    return flattenChildren(children.props.children);
  }
  return "";
}

const headingWithAnchor = (level) => ({ children, ...rest }) => {
  const Tag = `h${level}`;
  const text = flattenChildren(children);
  const id = slugify(text);
  return <Tag id={id} {...rest}>{children}</Tag>;
};

const MARKDOWN_COMPONENTS = {
  h1: headingWithAnchor(1),
  h2: headingWithAnchor(2),
  h3: headingWithAnchor(3),
  h4: headingWithAnchor(4),
  h5: headingWithAnchor(5),
  h6: headingWithAnchor(6),
};


export default function AdminDocsManual() {
  const { slug: routeSlug } = useParams();
  const { hash } = useLocation();
  const activeSlug = routeSlug || "compliance-manual";
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const { data } = await api.get(`/admin/docs/${activeSlug}`);
        if (!cancelled) setDoc(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load doc.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeSlug]);

  // Hash-scroll: after the doc renders (loading→false), scroll to the
  // anchor in the URL fragment if any. Defer 50ms so DOM nodes exist.
  useEffect(() => {
    if (loading || !hash) return;
    const id = hash.replace(/^#/, "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [loading, hash, activeSlug]);

  return (
    <section data-testid="admin-docs-page" className="space-y-5">
      <header className="space-y-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-cyan-300/80" />
          <h1 className="font-display text-2xl tracking-tight text-white">Ops docs</h1>
          <span className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-white/[0.08] bg-black/30 px-2 py-1 text-[10.5px] text-zinc-500 font-mono">
            <kbd className="rounded border border-white/[0.10] bg-white/[0.04] px-1">⌘</kbd>
            <kbd className="rounded border border-white/[0.10] bg-white/[0.04] px-1">K</kbd>
            to search headings
          </span>
        </div>
        <p className="text-[12.5px] text-zinc-500">
          Internal operating manuals for the team. Bookmark each page.
        </p>
      </header>

      <nav className="flex flex-wrap gap-1.5 border-b border-white/[0.06] pb-2">
        {TABS.map(({ slug, label, icon: Icon }) => (
          <NavLink
            key={slug}
            to={`/admin/docs/manual/${slug}`}
            data-testid={`admin-docs-tab-${slug}`}
            className={({ isActive }) => {
              const isCurrent = isActive || (slug === "compliance-manual" && !routeSlug);
              return `inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-[12px] transition-colors ${
                isCurrent
                  ? "border border-cyan-400/30 bg-cyan-400/[0.08] text-cyan-100"
                  : "border border-white/[0.06] text-zinc-300 hover:bg-white/[0.04]"
              }`;
            }}
          >
            <Icon className="h-3 w-3" /> {label}
          </NavLink>
        ))}
      </nav>

      <article
        data-testid="admin-docs-body"
        className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8"
      >
        {loading && (
          <div className="flex items-center gap-2 text-[12px] text-zinc-500">
            <Loader2 className="h-3 w-3 animate-spin" /> Loading…
          </div>
        )}

        {error && (
          <div
            data-testid="admin-docs-error"
            className="rounded-lg border border-rose-400/20 bg-rose-400/[0.03] p-3 text-rose-200 text-[12px] inline-flex items-center gap-2"
          >
            <AlertTriangle className="h-3 w-3" /> {error}
          </div>
        )}

        {doc && !loading && (
          <div className="prose-neptune">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={MARKDOWN_COMPONENTS}
            >
              {doc.markdown}
            </ReactMarkdown>
          </div>
        )}
      </article>
    </section>
  );
}
